import { useState, useEffect, useRef, FormEvent, useCallback, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { SCHEDULE_PATH } from "@/constants/links";
import ClutchBadge from "@/components/ClutchBadge";

const ReactMarkdown = lazy(() => import("react-markdown").then(m => ({ default: m.default })));

const words = ["Accelerate", "Future-Proof", "Streamline"];

const lineVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.4 + i * 0.2, duration: 0.5, ease: "easeOut" as const },
  }),
};

const SkeletonLoader = () => (
  <div className="space-y-4 animate-pulse">
    <div className="h-5 bg-muted/60 rounded-md w-3/4" />
    <div className="space-y-2.5">
      <div className="h-3.5 bg-muted/40 rounded w-full" />
      <div className="h-3.5 bg-muted/40 rounded w-[95%]" />
      <div className="h-3.5 bg-muted/40 rounded w-[85%]" />
    </div>
    <div className="space-y-2.5">
      <div className="h-3.5 bg-muted/40 rounded w-full" />
      <div className="h-3.5 bg-muted/40 rounded w-[90%]" />
      <div className="h-3.5 bg-muted/40 rounded w-[70%]" />
    </div>
    <div className="space-y-2 pl-4">
      <div className="h-3.5 bg-muted/40 rounded w-[80%]" />
      <div className="h-3.5 bg-muted/40 rounded w-[75%]" />
      <div className="h-3.5 bg-muted/40 rounded w-[65%]" />
    </div>
    <div className="space-y-2.5 pt-2">
      <div className="h-3.5 bg-muted/40 rounded w-[88%]" />
      <div className="h-3.5 bg-muted/40 rounded w-[60%]" />
    </div>
    <div className="flex justify-center pt-4">
      <div className="h-10 bg-muted/50 rounded-full w-48" />
    </div>
  </div>
);

const TYPEWRITER_PROMPTS = [
  "How can we adopt AI in our organization?",
  "We need to modernize our legacy stack...",
  "How can AI speed up our dev process?",
  "We need a team but lack tech leadership...",
];

const CHAR_TYPE_MS = 40;
const CHAR_ERASE_MS = 25;
const PAUSE_MS = 2000;

const useTypewriterPlaceholder = (active: boolean) => {
  const [displayed, setDisplayed] = useState("");
  const promptIdx = useRef(0);
  const charIdx = useRef(0);
  const phase = useRef<"typing" | "pausing" | "erasing">("typing");
  const prevActive = useRef(false);

  useEffect(() => {
    if (!active) {
      prevActive.current = false;
      return;
    }

    if (!prevActive.current) {
      promptIdx.current = 0;
      charIdx.current = 0;
      phase.current = "typing";
      setDisplayed("");
      prevActive.current = true;
    }

    let timer: ReturnType<typeof setTimeout>;

    const tick = () => {
      const prompt = TYPEWRITER_PROMPTS[promptIdx.current];

      if (phase.current === "typing") {
        charIdx.current++;
        setDisplayed(prompt.slice(0, charIdx.current));
        if (charIdx.current >= prompt.length) {
          phase.current = "pausing";
          timer = setTimeout(tick, PAUSE_MS);
        } else {
          timer = setTimeout(tick, CHAR_TYPE_MS);
        }
      } else if (phase.current === "pausing") {
        phase.current = "erasing";
        timer = setTimeout(tick, CHAR_ERASE_MS);
      } else {
        charIdx.current--;
        setDisplayed(prompt.slice(0, charIdx.current));
        if (charIdx.current <= 0) {
          promptIdx.current = (promptIdx.current + 1) % TYPEWRITER_PROMPTS.length;
          phase.current = "typing";
          timer = setTimeout(tick, CHAR_TYPE_MS + 200);
        } else {
          timer = setTimeout(tick, CHAR_ERASE_MS);
        }
      }
    };

    timer = setTimeout(tick, CHAR_TYPE_MS + 400);
    return () => clearTimeout(timer);
  }, [active]);

  return displayed;
};

const HeroSection = () => {
  const [wordIndex, setWordIndex] = useState(0);
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const responseRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const loadTimestamp = useRef(Date.now());
  const [honeypot, setHoneypot] = useState("");

  // Only start typewriter when the chat section is scrolled into view
  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const showTypewriter = !isFocused && !query && !hasSubmitted && isInView;
  const typewriterText = useTypewriterPlaceholder(showTypewriter);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % words.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (hasSubmitted && chatRef.current) {
      chatRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hasSubmitted]);

  const handleReset = () => {
    setQuery("");
    setResponse("");
    setHasSubmitted(false);
    setIsLoading(false);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
    chatRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToChat = () => {
    chatRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!query.trim() || isLoading) return;

    setHasSubmitted(true);
    setResponse("");
    setIsLoading(true);

    const timeSinceLoad = Date.now() - loadTimestamp.current;
    let accumulated = "";
    let capturedQueryId: string | null = null;

    try {
      const { streamChat } = await import("@/lib/streamChat");
      const sessionId = sessionStorage.getItem("analytics_session_id") || undefined;
      await streamChat({
        messages: [{ role: "user", content: query.trim() }],
        honeypot,
        loadTime: timeSinceLoad,
        sessionId,
        onQueryId: (id) => { capturedQueryId = id; },
        onDelta: (chunk) => { accumulated += chunk; setResponse(accumulated); },
        onDone: () => {
          setResponse(accumulated);
          setIsLoading(false);
          if (capturedQueryId && accumulated) {
            supabase.functions.invoke("save-chat-response", { body: { queryId: capturedQueryId, response: accumulated } }).catch((err) => {
              console.error("Failed to save chat response:", err);
            });
            supabase.functions.invoke("categorize-query", { body: { queryId: capturedQueryId, queryText: query.trim() } }).catch((err) => console.error("Categorization failed:", err));
          }
        },
        onError: (error) => { toast.error(error); setIsLoading(false); }
      });
    } catch {
      toast.error("Failed to connect. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* ── Section 1: Value Proposition Hero ── */}
      <section className="relative min-h-[75vh] pt-10 md:pt-16 flex items-center justify-center overflow-hidden">
        <div className="container mx-auto px-6 text-center relative z-10 max-w-4xl">
          <div className="space-y-10">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight"
            >
              Empowering your future with{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-primary">smart development.</span>
                <motion.span
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.6, duration: 0.5, ease: "easeOut" }}
                  className="absolute bottom-0 left-0 w-full h-[0.15em] bg-primary/20 origin-left rounded-full"
                />
              </span>
            </motion.h1>

            <div className="space-y-3 text-lg md:text-xl">
              {[
                { text: "Senior product leadership.", highlight: false },
                { text: "AI-enhanced development teams.", highlight: false },
                { text: "Outcomes that justify the investment.", highlight: true },
              ].map((line, i) => (
                <motion.p
                  key={i}
                  custom={i}
                  initial="hidden"
                  animate="visible"
                  variants={lineVariants}
                  className={
                    line.highlight
                      ? "text-foreground font-semibold text-xl md:text-2xl mt-4"
                      : "text-muted-foreground"
                  }
                >
                  {line.text}
                </motion.p>
              ))}

              <ClutchBadge />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
            >
              <Link
                to={SCHEDULE_PATH}
                className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground px-8 py-3 text-sm font-medium shadow-sm hover:bg-primary/90 transition-colors"
              >
                Schedule a Conversation
              </Link>
              <button
                onClick={scrollToChat}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-border text-muted-foreground px-8 py-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                Ask us anything
                <ArrowDown className="w-4 h-4" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Section 2: Interactive Chat ── */}
      <section ref={chatRef} className="relative py-24 md:py-32 bg-secondary/30">
        <div className="container mx-auto px-6 text-center relative z-10 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {!hasSubmitted && (
              <div className="mb-3 h-[1.1em] text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={words[wordIndex]}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ duration: 0.4 }}
                    className="block text-primary"
                  >
                    {words[wordIndex]}
                  </motion.span>
                </AnimatePresence>
              </div>
            )}

            {!hasSubmitted && (
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-10">
                your software with our expert{" "}
                <span className="font-semibold text-foreground whitespace-nowrap">
                  AI-advantaged dev teams
                </span>
              </p>
            )}

            <form onSubmit={handleSubmit} className={`relative max-w-2xl mx-auto ${hasSubmitted ? "mt-4" : ""}`}>
              <input
                type="text"
                name="website_url"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0, overflow: "hidden" }}
              />
              <div className="relative group">
                <Search className="absolute left-4 top-4 w-5 h-5 text-muted-foreground group-focus-within:text-primary transition-colors z-10" />
                {showTypewriter && (
                  <span className="absolute left-12 right-4 top-3 sm:top-4 text-base sm:text-lg text-muted-foreground pointer-events-none z-0 overflow-hidden whitespace-nowrap text-ellipsis">
                    {typewriterText}
                    <span className="inline-block ml-0.5 animate-pulse">|</span>
                  </span>
                )}
                <textarea
                  ref={textareaRef}
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); autoResize(); }}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
                  }}
                  disabled={isLoading}
                  rows={1}
                  className={`w-full pl-12 pr-4 py-3 sm:py-4 rounded-2xl border border-border bg-card text-foreground text-base sm:text-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all shadow-sm hover:shadow-md disabled:opacity-60 resize-none overflow-hidden ${!isFocused && !query ? 'animate-ring-pulse' : ''}`}
                  placeholder={isFocused || query ? "Type your question here..." : ""}
                />
              </div>
            </form>

            {!isFocused && !query && !hasSubmitted && (
              <p className="text-center text-xs text-muted-foreground/60 mt-3 animate-fade-in">
                ↑ Ask us anything — powered by AI
              </p>
            )}
          </motion.div>

          {hasSubmitted && (
            <div className="mt-8">
              <div
                ref={responseRef}
                className="max-w-2xl mx-auto text-left bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-sm"
              >
                {isLoading && !response && <SkeletonLoader />}
                <AnimatePresence>
                  {response && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                      className="prose prose-sm max-w-none text-foreground prose-headings:text-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-li:text-muted-foreground prose-blockquote:text-muted-foreground"
                    >
                      <Suspense fallback={<div className="animate-pulse h-20 bg-muted/40 rounded" />}>
                        <ReactMarkdown
                          components={{
                            a: ({ href, children }) => {
                              const isCalendly = href?.includes("calendly.com");
                              if (isCalendly) {
                                return (
                                  <span className="not-prose flex justify-center mt-6 mb-2">
                                    <a href={href} target="_blank" rel="noopener noreferrer"
                                      className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground px-8 py-3 text-sm font-medium shadow-sm hover:bg-primary/90 transition-colors">
                                      {children}
                                    </a>
                                  </span>
                                );
                              }
                              const isInternal = href?.startsWith("/");
                              return (
                                <a href={href} {...(!isInternal && { target: "_blank", rel: "noopener noreferrer" })}
                                  className="text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
                                  {children}
                                </a>
                              );
                            },
                          }}
                        >
                          {response}
                        </ReactMarkdown>
                      </Suspense>
                    </motion.div>
                  )}
                </AnimatePresence>
                {response && !isLoading && (
                  <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link to={SCHEDULE_PATH}
                      className="inline-flex items-center justify-center rounded-full border border-primary text-primary px-6 py-2.5 text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors">
                      Schedule a meeting
                    </Link>
                    <button onClick={handleReset}
                      className="inline-flex items-center justify-center rounded-full border border-border text-muted-foreground px-6 py-2.5 text-sm font-medium hover:bg-accent hover:text-foreground transition-colors">
                      Ask another question
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default HeroSection;
